import { createEffect } from '@databases/selections';
import { type SelectionsApiContract } from '@databases/selections-api-types';

interface CellPosition {
  row: { index: number };
  col: { index: number };
}

export interface CellEditingEffectDeps {
  selectionsApi: SelectionsApiContract;
  /**
   * Called when editing starts or stops on a cell.
   * `context` indicates where the edit is happening — 'table' or 'panel'.
   * On stop, `context` reflects where the edit was opened (used to close the right editor).
   */
  notifyCellEditing: (
    entryId: string,
    fieldId: string,
    editing: boolean,
    context: 'table' | 'panel'
  ) => void;
}

export const createCellEditingEffect = createEffect(
  ({ selectionsApi, notifyCellEditing }: CellEditingEffectDeps) => {
    let isOpen = false;
    let openPosition: CellPosition | undefined;
    let openContext: 'table' | 'panel' = 'table';

    const getCellIds = (position: CellPosition) =>
      selectionsApi.resolvePositionToIds(position, 'cell');

    const setCellEditingState = (
      position: CellPosition,
      editing: boolean,
      context: 'table' | 'panel'
    ) => {
      const { entryId, fieldId } = getCellIds(position);

      if (entryId && fieldId) {
        notifyCellEditing(entryId, fieldId, editing, context);
      }
    };

    return {
      dispose() {
        if (isOpen && openPosition) {
          setCellEditingState(openPosition, false, openContext);
        }

        isOpen = false;
        openPosition = undefined;
      },

      deltaTypes: ['cellEditRequested', 'cellEditStopped'],

      onDelta(delta) {
        if (delta.kind === 'cellEditRequested') {
          const { entryId, fieldId } = getCellIds(delta.position);

          if (!fieldId || !entryId) {
            return;
          }

          const context = delta.editingContext;

          setCellEditingState(delta.position, true, context);
          isOpen = true;
          openPosition = delta.position;
          openContext = context;
        } else if (delta.kind === 'cellEditStopped') {
          if (isOpen && openPosition) {
            setCellEditingState(openPosition, false, openContext);
          }

          isOpen = false;
          openPosition = undefined;
        }
      },
    };
  }
);
